import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Workflow, Loader2, Play, ChevronRight, GitCommitHorizontal, ShieldCheck, Bot, UserCheck, Plus, Trash2, Wrench, GitBranch, GitFork } from "lucide-react";
import { toast } from "sonner";

const STEP_ICON = { AGENT_TASK: Bot, REVIEW: ShieldCheck, HUMAN_APPROVAL: UserCheck, CONDITION: GitBranch, PARALLEL: GitFork };
const TOP_TYPES = ["AGENT_TASK", "REVIEW", "HUMAN_APPROVAL", "CONDITION", "PARALLEL"];
const SUB_TYPES = ["AGENT_TASK", "REVIEW", "HUMAN_APPROVAL"];
const FIELDS = ["project.stage", "project.code", "project.industry", "project.health", "cost.total"];
const OPS = ["eq", "ne", "contains", "lt", "lte", "gt", "gte"];
const newSub = () => ({ type: "AGENT_TASK", name: "", required_skill: "", min_level: 4 });

function SubSteps({ steps, onChange, skills, testid }) {
  const { t } = useI18n();
  const set = (i, k, v) => onChange(steps.map((s, x) => (x === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2 pl-3 border-l border-[#1E293B]">
      {steps.map((s, i) => (
        <div key={i} className="space-y-1.5" data-testid={`${testid}-${i}`}>
          <div className="flex items-center gap-2">
            <select value={s.type} onChange={(e) => set(i, "type", e.target.value)} className="input-dark h-8 px-2 text-xs">
              {SUB_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
            <input value={s.name} onChange={(e) => set(i, "name", e.target.value)} placeholder={t("fl.stepNamePh")} className="input-dark h-8 px-2 text-xs flex-1" />
            <button onClick={() => onChange(steps.filter((_, x) => x !== i))} className="text-[#FCA5A5]"><Trash2 size={13} /></button>
          </div>
          {(s.type === "AGENT_TASK" || s.type === "REVIEW") && (
            <div className="flex items-center gap-2">
              <select value={s.required_skill} onChange={(e) => set(i, "required_skill", e.target.value)} className="input-dark h-8 px-2 text-xs flex-1">
                <option value="">{t("fl.requiredSkillPh")}</option>{skills.map((sk) => <option key={sk.code} value={sk.code}>{sk.name}</option>)}
              </select>
              <select value={s.min_level} onChange={(e) => set(i, "min_level", Number(e.target.value))} className="input-dark h-8 px-2 text-xs">
                {[1, 2, 3, 4, 5].map((l) => <option key={l} value={l}>{`L${l}`}</option>)}
              </select>
            </div>
          )}
        </div>
      ))}
      <button onClick={() => onChange([...steps, newSub()])} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"><Plus size={12} /> {t("fl.addStep")}</button>
    </div>
  );
}

export default function Workflows() {
  const [templates, setTemplates] = useState(null);
  const [runs, setRuns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [active, setActive] = useState(null);
  const [project, setProject] = useState("P001");
  const [objective, setObjective] = useState("");
  const [running, setRunning] = useState(false);
  const [builder, setBuilder] = useState(false);
  const [wf, setWf] = useState({ title: "", description: "", steps: [newSub()] });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  const load = () => { api.workflows().then((w) => setTemplates(w.filter((x) => x.runnable))); api.workflowRuns().then(setRuns); };
  useEffect(() => { load(); api.projects().then(setProjects); api.skills().then(setSkills); }, []);

  const run = async () => {
    setRunning(true);
    try { const res = await api.runWorkflow(active.code, { project_code: project, objective: objective || active.title }); toast.success(t("fl.executed")); setActive(null); setObjective(""); navigate(`/workflows/runs/${res.run.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || t("fl.runFailed")); } finally { setRunning(false); }
  };

  const setStep = (i, patch) => setWf((w) => ({ ...w, steps: w.steps.map((s, x) => (x === i ? { ...s, ...patch } : s)) }));
  const addTop = (type) => {
    const base = { type, name: "" };
    if (type === "AGENT_TASK" || type === "REVIEW") Object.assign(base, { required_skill: "", min_level: 4 });
    if (type === "CONDITION") Object.assign(base, { predicate: { field: "project.industry", op: "contains", value: "Wellness" }, if_true: [newSub()], if_false: [] });
    if (type === "PARALLEL") Object.assign(base, { join: "ALL", branches: [[newSub()], [newSub()]] });
    setWf((w) => ({ ...w, steps: [...w.steps, base] }));
  };

  const save = async () => {
    if (!wf.title.trim()) return toast.error(t("fl.titleRequired"));
    const check = (arr) => {
      for (const s of arr) {
        if (!s.name?.trim()) return t("fl.stepNeedsName");
        if ((s.type === "AGENT_TASK" || s.type === "REVIEW") && !s.required_skill) return t("fl.stepNeedsSkill", { name: s.name });
        if (s.type === "CONDITION") { const e = check(s.if_true || []) || check(s.if_false || []); if (e) return e; }
        if (s.type === "PARALLEL") { for (const br of s.branches || []) { const e = check(br); if (e) return e; } }
      }
      return null;
    };
    const err = check(wf.steps);
    if (err) return toast.error(err);
    setSaving(true);
    try { await api.createWorkflow(wf); toast.success(t("fl.created")); setBuilder(false); setWf({ title: "", description: "", steps: [newSub()] }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("fl.saveFailed")); } finally { setSaving(false); }
  };

  if (!templates) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="mono-label">{t("fl.label")}</p>
          <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><Workflow className="text-[#2563EB]" /> {t("fl.title")}</h1>
          <p className="text-sm text-[#94A3B8] mt-1">{t("fl.subtitle")}</p>
        </div>
        <button onClick={() => setBuilder(true)} className="btn-primary h-10 px-4 text-sm flex items-center gap-2" data-testid="workflow-builder-open"><Plus size={16} /> {t("fl.new")}</button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {templates.map((w) => (
          <div key={w.code} className="m-card p-5" data-testid={`workflow-template-${w.code}`}>
            <div className="flex items-center justify-between"><span className="mono-label">{w.custom ? "CUSTOM" : w.code}</span><span className="text-[10px] text-[#64748B]">{t("fl.steps", { n: w.steps.length })}</span></div>
            <h2 className="font-display text-lg font-bold text-white mt-2">{w.title}</h2>
            <p className="text-xs text-[#94A3B8] mt-1">{w.description}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {w.steps.map((s, i) => { const Icon = STEP_ICON[s.type] || Bot; return (
                <div key={i} className="flex items-center gap-1.5">{i > 0 && <ChevronRight size={12} className="text-[#334155]" />}<span className="flex items-center gap-1 text-[10px] bg-[#0B0F19] border border-[#1E293B] rounded-md px-2 py-1 text-[#94A3B8]"><Icon size={11} className="text-[#2563EB]" />{s.name}</span></div>
              ); })}
            </div>
            <button onClick={() => setActive(w)} className="btn-primary h-9 px-4 text-sm flex items-center gap-2 mt-4" data-testid={`workflow-run-${w.code}`}><Play size={14} /> {t("fl.runWf")}</button>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-white mb-3 flex items-center gap-2"><GitCommitHorizontal size={18} className="text-[#2563EB]" /> {t("fl.recentRuns")}</h2>
        {runs.length === 0 ? <div className="m-card p-6 text-sm text-[#64748B]">{t("fl.noRuns")}</div> : (
          <div className="space-y-2">{runs.map((r) => (
            <div key={r.id} className="m-card m-card-hover p-4 flex items-center justify-between cursor-pointer" onClick={() => navigate(`/workflows/runs/${r.id}`)} data-testid={`workflow-run-card-${r.id}`}>
              <div><p className="text-white text-sm font-medium">{r.title}</p><p className="text-[11px] text-[#64748B] font-mono">{r.code} · {r.project_code} · ${r.cost}</p></div>
              <div className="flex items-center gap-2"><StatusPill status={r.status === "AWAITING_APPROVAL" ? "APPROVAL_REQUIRED" : r.status} label={r.status} /><ChevronRight size={16} className="text-[#64748B]" /></div>
            </div>
          ))}</div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0]">
          <DialogHeader><DialogTitle className="font-display text-white">{t("fl.runDialogTitle", { t: active?.title })}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select value={project} onChange={(e) => setProject(e.target.value)} className="input-dark w-full h-10 px-3 text-sm" data-testid="workflow-run-project">{projects.map((p) => <option key={p.code} value={p.code}>{p.code} · {p.name}</option>)}</select>
            <textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={2} placeholder={t("fl.objectivePh")} className="input-dark w-full p-3 text-sm" data-testid="workflow-run-objective" />
            <button onClick={run} disabled={running} className="btn-primary w-full h-10 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="workflow-run-submit">{running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} {t("fl.executeSteps")}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={builder} onOpenChange={setBuilder}>
        <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0] max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-white flex items-center gap-2"><Wrench size={17} className="text-[#2563EB]" /> {t("fl.builder")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={wf.title} onChange={(e) => setWf({ ...wf, title: e.target.value })} placeholder={t("fl.wfTitlePh")} className="input-dark w-full h-10 px-3 text-sm" data-testid="wf-builder-title" />
            <input value={wf.description} onChange={(e) => setWf({ ...wf, description: e.target.value })} placeholder={t("fl.wfDescPh")} className="input-dark w-full h-10 px-3 text-sm" data-testid="wf-builder-desc" />
            <p className="mono-label pt-1">{t("fl.stepsLabel")}</p>

            {wf.steps.map((s, i) => (
              <div key={i} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 space-y-2" data-testid={`wf-builder-step-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="mono-label">{i + 1}</span>
                  <select value={s.type} onChange={(e) => { const tp = e.target.value; const b = { type: tp, name: s.name }; if (tp === "AGENT_TASK" || tp === "REVIEW") Object.assign(b, { required_skill: "", min_level: 4 }); if (tp === "CONDITION") Object.assign(b, { predicate: { field: "project.industry", op: "contains", value: "Wellness" }, if_true: [newSub()], if_false: [] }); if (tp === "PARALLEL") Object.assign(b, { join: "ALL", branches: [[newSub()], [newSub()]] }); setStep(i, b); }} className="input-dark h-9 px-2 text-xs" data-testid={`wf-step-type-${i}`}>
                    {TOP_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                  <input value={s.name} onChange={(e) => setStep(i, { name: e.target.value })} placeholder={t("fl.stepNamePh")} className="input-dark h-9 px-2 text-xs flex-1" data-testid={`wf-step-name-${i}`} />
                  {wf.steps.length > 1 && <button onClick={() => setWf((w) => ({ ...w, steps: w.steps.filter((_, x) => x !== i) }))} className="text-[#FCA5A5]" data-testid={`wf-step-remove-${i}`}><Trash2 size={15} /></button>}
                </div>

                {(s.type === "AGENT_TASK" || s.type === "REVIEW") && (
                  <div className="flex items-center gap-2">
                    <select value={s.required_skill} onChange={(e) => setStep(i, { required_skill: e.target.value })} className="input-dark h-9 px-2 text-xs flex-1" data-testid={`wf-step-skill-${i}`}><option value="">{t("fl.requiredSkillPh")}</option>{skills.map((sk) => <option key={sk.code} value={sk.code}>{sk.name}</option>)}</select>
                    <select value={s.min_level} onChange={(e) => setStep(i, { min_level: Number(e.target.value) })} className="input-dark h-9 px-2 text-xs" data-testid={`wf-step-level-${i}`}>{[1, 2, 3, 4, 5].map((l) => <option key={l} value={l}>{`min L${l}`}</option>)}</select>
                  </div>
                )}

                {s.type === "CONDITION" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2" data-testid={`wf-cond-${i}`}>
                      <select value={s.predicate.field} onChange={(e) => setStep(i, { predicate: { ...s.predicate, field: e.target.value } })} className="input-dark h-8 px-2 text-xs flex-1">{FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}</select>
                      <select value={s.predicate.op} onChange={(e) => setStep(i, { predicate: { ...s.predicate, op: e.target.value } })} className="input-dark h-8 px-2 text-xs">{OPS.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                      <input value={s.predicate.value} onChange={(e) => setStep(i, { predicate: { ...s.predicate, value: e.target.value } })} placeholder="value" className="input-dark h-8 px-2 text-xs w-24" />
                    </div>
                    <p className="text-[10px] text-[#64748B]">{t("fl.ifTrue")}</p>
                    <SubSteps steps={s.if_true} onChange={(v) => setStep(i, { if_true: v })} skills={skills} testid={`wf-if-true-${i}`} />
                    <p className="text-[10px] text-[#64748B]">{t("fl.ifFalse")}</p>
                    <SubSteps steps={s.if_false} onChange={(v) => setStep(i, { if_false: v })} skills={skills} testid={`wf-if-false-${i}`} />
                  </div>
                )}

                {s.type === "PARALLEL" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2"><span className="text-[10px] text-[#64748B]">Join</span>
                      <select value={s.join} onChange={(e) => setStep(i, { join: e.target.value })} className="input-dark h-8 px-2 text-xs" data-testid={`wf-parallel-join-${i}`}><option value="ALL">ALL</option><option value="ANY">ANY</option></select>
                    </div>
                    {s.branches.map((br, bi) => (
                      <div key={bi}>
                        <div className="flex items-center justify-between"><p className="text-[10px] text-[#64748B]">{t("fl.branch", { n: bi + 1 })}</p>{s.branches.length > 2 && <button onClick={() => setStep(i, { branches: s.branches.filter((_, x) => x !== bi) })} className="text-[#FCA5A5]"><Trash2 size={12} /></button>}</div>
                        <SubSteps steps={br} onChange={(v) => setStep(i, { branches: s.branches.map((b, x) => (x === bi ? v : b)) })} skills={skills} testid={`wf-branch-${i}-${bi}`} />
                      </div>
                    ))}
                    <button onClick={() => setStep(i, { branches: [...s.branches, [newSub()]] })} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1"><Plus size={12} /> {t("fl.addBranch")}</button>
                  </div>
                )}
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              {TOP_TYPES.map((tp) => <button key={tp} onClick={() => addTop(tp)} className="btn-ghost h-8 px-3 text-[11px] flex items-center gap-1" data-testid={`wf-add-${tp}`}><Plus size={13} /> {tp}</button>)}
            </div>
            <button onClick={save} disabled={saving} className="btn-primary w-full h-10 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="wf-builder-save">{saving ? <Loader2 size={15} className="animate-spin" /> : <Wrench size={15} />} {t("fl.createWf")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
